// Your plans.
//
// The list of plans you have access to, and a control to create one. "Creating asks for a
// name and nothing else" — one field, and the API's `POST /v1/plans` accepts one field and
// refuses a body with any second one.
//
// A plan cannot be renamed here, and this row is otherwise just a link — no rename or
// delete control lives on the list itself. `OperatorPlanDelete` below is a dev-only
// exception, absent from every build a family ever sees, gated the same two ways
// `PlanScreen.tsx`'s debug panel is (see its own header comment).
//
// Deleting a DEMO plan is a real, family-facing control, and it lives here on the row
// beside the plan it deletes rather than inside the plan. A control that ends the thing you
// are looking at does not belong in that thing's own header, and it was there: three
// controls of three different shapes sat in a row across the top of a demo — a mark, a
// link and a destructive button.

import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ApiFailure, type ApiClient } from '../api/client.js';
import type { Plan } from '../api/contract.js';
import { identitySnapshot, subscribeIdentity } from '../clerk.js';
import { DemoDeleteControl } from '../components/DemoDeleteControl.js';
import { dayOnly } from '../format.js';
import { session } from '../session.js';

// Vite removes this import and its chunk from builds where the operator flag is absent,
// the same gate `PlanScreen.tsx` uses for its own operator panel.
const OperatorPlanDelete =
  import.meta.env.VITE_MOR_OPERATOR_ENABLED === 'true'
    ? lazy(() =>
        import('../operator/PlanDelete.js').then((module) => ({
          default: module.PlanDeleteControl,
        })),
      )
    : null;

export type PlansProps = {
  client: ApiClient;
};

export function Plans({ client }: PlansProps): JSX.Element {
  const [plans, setPlans] = useState<readonly Plan[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [waiting, setWaiting] = useState(false);
  // Collapsed by default — a "+ New plan" button next to the heading reveals the form in
  // place, framed like the cards below it, rather than a form permanently open at the
  // bottom of the list (`docs/archived/13-visual-refresh.md`, `plans-list-new-plan.jpg`).
  const [expanded, setExpanded] = useState(false);
  const generation = useRef(0);
  const identity = useSyncExternalStore(
    subscribeIdentity,
    identitySnapshot,
    identitySnapshot,
  );
  // Someone in no plans yet is offered "<first name>'s plan" as the name; anyone else
  // starts with an empty field.
  const firstName = identity.name?.trim().split(/\s+/u)[0] ?? '';
  const suggestedName =
    plans !== null && plans.length === 0 && firstName !== '' ? `${firstName}’s plan` : '';

  const load = useCallback(async (): Promise<void> => {
    // A slow first read must not overwrite a fast retry: last started wins.
    const mine = (generation.current += 1);
    try {
      const listed = await client.listPlans();
      if (mine !== generation.current) {
        return;
      }
      // Newest first. A stand-in for sorting by actual last activity, which the API has no
      // field for yet — see `docs/parking-lot.md`'s "A real 'last updated' value for plans".
      setPlans([...listed].sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
      setProblem(null);
      setWaiting(false);
    } catch (error) {
      if (mine !== generation.current) {
        return;
      }
      // The plan screen's split, applied to the list. A 401 alone is a stale bearer
      // waiting on the vendor's next refresh, so it reads as still signing in; every
      // other unnamed failure — a rate limit, a server error — retries on the same
      // triggers but says honestly that the load failed, because "signing you back in"
      // over a 500 is a claim nothing is working on.
      if (error instanceof ApiFailure && error.code === 'unreachable') {
        setProblem('Couldn’t reach the server. Try again in a moment.');
        setWaiting(false);
      } else if (
        error instanceof ApiFailure &&
        (error.status === 404 || error.status === 400)
      ) {
        setProblem('Couldn’t load your plans.');
        setWaiting(false);
      } else if (error instanceof ApiFailure && error.status === 401) {
        setProblem(null);
        setWaiting(true);
      } else {
        setProblem('That didn’t load. It will keep trying.');
        setWaiting(false);
      }
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load]);

  // A fresh bearer and a returning network re-read the list — but only while it has never
  // rendered, so the vendor's 50-second token republish does not become a refetch schedule.
  useEffect(() => {
    const retry = (): void => {
      if (plans === null) {
        void load();
      }
    };
    const unsubscribe = session.subscribe(retry);
    window.addEventListener('online', retry);
    return () => {
      unsubscribe();
      window.removeEventListener('online', retry);
    };
  }, [load, plans]);

  return (
    <main className="screen screen--plans">
      <div className="plansHead">
        <h1 className="screen__title">Your plans</h1>
        {/* No "+ Demo" button for now: demos do not work with the current screens, and
            are not worth improving until those screens support more than one person. The
            demo pages still work at `/demo` for anyone who goes there directly. */}
        {/* The button goes while the form is open. Leaving it behind put a second way to
            start something beside a form already asking for a name. The form closes
            itself instead.

            HIDDEN, NOT REMOVED. It is the tallest thing in the row, so taking it out of the
            layout let the row shrink and the heading beside it visibly jumped. A pinned
            height was tried and did not hold, because the row is taller than the thumb
            target the button carries. Left in place and made invisible, the row cannot
            change at all — and `aria-hidden` with `inert` keeps it out of reach of a reader
            and a keyboard while it is not offered. */}
        <span
          className={
            expanded
              ? 'plansHead__actions plansHead__actions--hidden'
              : 'plansHead__actions'
          }
          aria-hidden={expanded}
          {...(expanded ? { inert: '' } : {})}
        >
          <button
            type="button"
            className="button button--outline"
            onClick={() => setExpanded(true)}
          >
            + New plan
          </button>
        </span>
      </div>
      {problem === null ? null : (
        <p className="screen__problem" role="status">
          {problem}
        </p>
      )}
      {waiting ? (
        <p className="screen__loading" role="status">
          Signing you back in…
        </p>
      ) : null}
      {expanded ? (
        <NewPlan
          client={client}
          suggestedName={suggestedName}
          onCreated={load}
          onDone={() => setExpanded(false)}
        />
      ) : null}
      {plans === null ? null : plans.length === 0 ? (
        <p className="screen__empty">You aren’t in any plans yet.</p>
      ) : (
        <ul className="planList">
          {plans.map((plan) => (
            <li key={plan.id} className="planList__item">
              <Link className="planList__link" to={`/plans/${plan.id}`}>
                <span className="planList__info">
                  <span className="planList__name">
                    {plan.name}
                    {plan.isDemo ? <span className="demoMark">Demo</span> : null}
                  </span>
                  <span className="planList__date">
                    Started {dayOnly(plan.createdAt)}
                  </span>
                </span>
              </Link>
              {plan.isDemo ? (
                <DemoDeleteControl
                  client={client}
                  planId={plan.id}
                  onDeleted={() => void load()}
                />
              ) : null}
              {/* Not for a demo: it has its own delete just above, which any person may use
                  on a demo they started. Rendering both put two identical buttons on one
                  row in a development build. */}
              {OperatorPlanDelete === null ||
              plan.isDemo ||
              !plan.viewerIsPrincipal ? null : (
                <Suspense fallback={null}>
                  <OperatorPlanDelete
                    client={client}
                    planId={plan.id}
                    planName={plan.name}
                    onDeleted={() => {
                      void load();
                    }}
                  />
                </Suspense>
              )}
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

function NewPlan({
  client,
  suggestedName,
  onCreated,
  onDone,
}: {
  client: ApiClient;
  suggestedName: string;
  onCreated: () => Promise<void>;
  onDone: () => void;
}): JSX.Element {
  const [name, setName] = useState(suggestedName);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const navigate = useNavigate();

  const create = async (): Promise<void> => {
    const trimmed = name.trim();
    if (trimmed === '' || busy) {
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      const plan = await client.createPlan(trimmed);
      setName('');
      await onCreated();
      onDone();
      navigate(`/plans/${plan.id}`);
    } catch {
      setProblem('That plan wasn’t created. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="newPlan"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void create();
      }}
    >
      <div className="newPlan__head">
        <label className="newPlan__label" htmlFor="new-plan-name">
          Start a new plan
        </label>
        <button
          type="button"
          className="iconButton newPlan__close"
          aria-label="Close"
          onClick={onDone}
        >
          ×
        </button>
      </div>
      <div className="newPlan__row">
        <input
          id="new-plan-name"
          className="newPlan__field"
          value={name}
          placeholder="What should it be called?"
          autoComplete="off"
          autoFocus
          onChange={(event) => setName(event.target.value)}
        />
        <button
          type="submit"
          className="button button--primary"
          disabled={busy || name.trim() === ''}
        >
          Create
        </button>
      </div>
      {problem === null ? null : (
        <p className="newPlan__problem" role="status">
          {problem}
        </p>
      )}
    </form>
  );
}
